"use client";

import { EstateCard } from "@/components/hlidaci-pes/EstateCard";
import { WatchDogSelectionPanelCard } from "@/components/hlidaci-pes/watchdog-page/WatchDogSelectionPanelCard";
import { ListingCarousel } from "@/components/index/fragments/ListingCarousel";
import { NewWatchdogDialog } from "@/components/modals/CreateNewWatchDog";
import { Button } from "@/components/ui/Button";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { useSessionContext } from "@/context/SessionContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import type { ReactNode } from "react";

function formatTemplate(
  template: string,
  values: Record<string, string | number | null | undefined>,
) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value ?? "")),
    template,
  );
}

function getWatchDogCountLabel(
  count: number,
  copy: {
    oneWatchdog: string;
    watchdogCountFew: string;
    watchdogCountMany: string;
  },
) {
  if (count === 0 || count > 4) {
    return formatTemplate(copy.watchdogCountMany, { count });
  }

  if (count === 1) return copy.oneWatchdog;

  return formatTemplate(copy.watchdogCountFew, { count });
}

export function HomeWatchDogSummary({
  headerAction,
}: {
  headerAction?: ReactNode;
}) {
  const { language } = useLanguage();
  const { PROFILE_ITEMS } = useAppContext();
  const { latestEstateOffers } = useSessionContext();
  const {
    userInfoGlobal,
    selectedDog,
    setSelectedDog,
    isLoadingWatchDogs,
    isLoadingResults,
    membershipInfo,
    watchdogs,
    estateOffers,
    isDisabled,
  } = useWatchDogContext();
  const {
    LOGGED_IN_ITEMS,
    setIsOpen,
    handleDiscardChanges,
    selectedItem,
    setSelectedItem,
  } = useMenuItemsContext();
  const copy = {
    cs: {
      oneWatchdog: "1 hlídací pes",
      watchdogCountFew: "{count} hlídací psi",
      watchdogCountMany: "{count} hlídacích psů",
      loadingMembership: "Načítám informace o členství.",
      limitReached:
        "V členství {membership} máte vyčerpán počet hlídacích psů.",
      canCreate: "Můžete vytvořit až {count} {label}",
      viewFull: "Zobrazit všechny hlídací psy",
      latestListings: "Poslední nalezené inzeráty",
      previous: "Předchozí",
      next: "Další",
      noLatestListings: "Zatím bez nalezených inzerátů",
      loadingLatestListings: "Načítám poslední inzeráty…",
    },
    en: {
      oneWatchdog: "1 watchdog",
      watchdogCountFew: "{count} watchdogs",
      watchdogCountMany: "{count} watchdogs",
      loadingMembership: "Loading membership information.",
      limitReached:
        "Your {membership} membership has reached its watchdog limit.",
      canCreate: "You can create up to {count} watchdogs",
      viewFull: "View all watchdogs",
      latestListings: "Latest found listings",
      previous: "Previous",
      next: "Next",
      noLatestListings: "No listings found yet",
      loadingLatestListings: "Loading latest listings…",
    },
  }[language];
  const watchDogList = watchdogs ?? [];
  const fallbackLatestOffers = [...(estateOffers ?? [])]
    .sort((a, b) => b.id - a.id)
    .slice(0, 5);
  const displayedLatestOffers = latestEstateOffers.length
    ? latestEstateOffers
    : fallbackLatestOffers;
  const latestOffersKey = displayedLatestOffers
    .map((offer) => offer.id)
    .join("-");
  const isMembershipLoading = membershipInfo == null && isLoadingWatchDogs;
  const tooltip = isMembershipLoading
    ? copy.loadingMembership
    : isDisabled
      ? formatTemplate(copy.limitReached, {
          membership: userInfoGlobal?.membership,
        })
      : formatTemplate(copy.canCreate, {
          count: membershipInfo?.maxWatchDogs,
          label: membershipInfo?.maxWatchDogsString,
        });

  const handleAddWatchDogClick = () => {
    if (isMembershipLoading) return;

    if (selectedItem != LOGGED_IN_ITEMS[0]) {
      handleDiscardChanges();
    }
    setSelectedItem(isDisabled ? PROFILE_ITEMS[0] : LOGGED_IN_ITEMS[0]);
    setIsOpen(true);
  };

  return (
    <section className="relative z-10 !p-0">
      <WatchDogSelectionPanelCard
        watchdogs={watchDogList}
        watchDogCountLabel={getWatchDogCountLabel(watchDogList.length, copy)}
        isLoading={isLoadingWatchDogs}
        selectedWatchDogId={selectedDog?.id ?? null}
        onSelectWatchDog={(watchDog) =>
          setSelectedDog(selectedDog?.id === watchDog.id ? null : watchDog)
        }
        tooltip={tooltip}
        isMembershipLoading={isMembershipLoading}
        onAddWatchDogClick={handleAddWatchDogClick}
        emptyContent={
          watchDogList.length === 0 && !isMembershipLoading && !isDisabled ? (
            <NewWatchdogDialog />
          ) : undefined
        }
        latestOffers={displayedLatestOffers}
        latestContent={
          <div className="relative h-[240px] min-w-0 max-w-full overflow-hidden [&_.demo-carousel-wrap]:h-full [&_.demo-carousel-wrap]:min-w-0 [&_.demo-carousel-wrap]:max-w-full [&_.demo-carousel-wrap]:overflow-hidden [&_.demo-slide]:!h-[240px] [&_.demo-slide]:!rounded-none [&_.demo-swiper]:!h-full [&_.demo-swiper]:!w-full [&_.demo-swiper]:!max-w-full [&_.demo-swiper]:!rounded-none [&_.listing-next-home-watchdogs]:!right-3 [&_.listing-prev-home-watchdogs]:!left-3 [&_.swiper]:!pb-0">
            <span className="app-pill-overlay">{copy.latestListings}</span>
            <ListingCarousel
              key={latestOffersKey || "latest-loading"}
              offers={displayedLatestOffers}
              navigationId="home-watchdogs"
              previousLabel={copy.previous}
              nextLabel={copy.next}
              renderOffer={(offer) => (
                <EstateCard
                  estateOffer={offer}
                  isMainPage={true}
                  editSpecificResult={() => {}}
                />
              )}
              emptyContent={
                <div className="flex h-[240px] items-center justify-center px-6 text-center text-sm text-softiq-primary/50 dark:text-white/50">
                  {isLoadingResults
                    ? copy.loadingLatestListings
                    : copy.noLatestListings}
                </div>
              }
              showPagination={false}
              loop={false}
            />
          </div>
        }
        showSelectedDetails
        summaryOffers={estateOffers ?? []}
        isSummaryLoading={isLoadingResults}
        footer={
          <Button asChild variant="default" size="compact">
            <Link
              href="/hlidaci-pes"
              data-tour="dashboard-watchdog-details-link"
            >
              {copy.viewFull}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          </Button>
        }
        headerAction={headerAction}
      />
    </section>
  );
}
